/**
 * ForbiddenError is thrown when a user is not authorized to access a resource.
 * 权限错误
 */
export class ForbiddenError extends Error {
	readonly statusCode = 403;

	constructor(message = "Forbidden") {
		super(message);
		this.name = "ForbiddenError";
	}
}

/**
 * InsufficientCreditsError is thrown when a user has no credits left
 * for a guarded action. This is a safe business error and can be
 * surfaced to the client directly.
 */
export class InsufficientCreditsError extends Error {
	readonly statusCode = 402;

	constructor(message = "INSUFFICIENT_CREDITS") {
		super(message);
		this.name = "InsufficientCreditsError";
	}
}

/**
 * FileSizeLimitError is thrown when a free-plan user tries to upload a file
 * that exceeds the plan's maximum size.
 */
export class FileSizeLimitError extends Error {
	readonly statusCode = 413;

	constructor(message = "FILE_SIZE_LIMIT_EXCEEDED") {
		super(message);
		this.name = "FileSizeLimitError";
	}
}

/**
 * DatabaseError wraps DB errors to prevent internal details from leaking to the frontend.
 * The original error is preserved in `cause` for server-side logging.
 * 数据库错误
 */
export class DatabaseError extends Error {
	readonly statusCode = 500;

	constructor(message = "数据库操作失败，请稍后再试", cause?: unknown) {
		super(message, { cause });
		this.name = "DatabaseError";
	}
}

export class MissingKeyError extends Error {
	readonly statusCode = 500;

	constructor(message = "缺少秘钥，请联系开发者", cause?: unknown) {
		super(message, { cause });
		this.name = "MissingKeyError";
	}
}

/**
 * Error codes for upstream AI provider failures.
 * 上游 AI 服务商错误码
 */
export const UpstreamProviderErrorCode = {
	/** Provider balance/billing exhausted (e.g. fal.ai 403) */
	BALANCE_EXHAUSTED: "BALANCE_EXHAUSTED",
	/** Cannot reach the upstream provider */
	CONNECTION_FAILED: "CONNECTION_FAILED",
} as const;

export type UpstreamProviderErrorCode =
	(typeof UpstreamProviderErrorCode)[keyof typeof UpstreamProviderErrorCode];

const defaultMessages: Record<UpstreamProviderErrorCode, string> = {
	BALANCE_EXHAUSTED: "上游服务商余额不足，请联系开发者。",
	CONNECTION_FAILED: "AI 服务商连接失败，请稍后重试。",
};

/**
 * UpstreamProviderError is thrown when an external AI provider returns a
 * non-transient infrastructure error (billing exhausted, unreachable, etc.).
 * 上游服务商错误
 */
export class UpstreamProviderError extends Error {
	readonly statusCode = 502;
	readonly code: UpstreamProviderErrorCode;

	constructor(code: UpstreamProviderErrorCode, message?: string, cause?: unknown) {
		super(message ?? defaultMessages[code], { cause });
		this.name = "UpstreamProviderError";
		this.code = code;
	}
}
